import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import ProgressBar from '../components/ProgressBar';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { productImageMap, defaultProductMeta } from '../lib/productMeta';

export const ProductSelection = ({ order, setOrder }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProducts = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('products')
      .select('*')
      .neq('is_visible', false) // hide explicitly hidden products
      .order('price', { ascending: true });

    if (fetchError) {
      setError('Kon producten niet laden');
    } else {
      const enriched = data.map(p => {
        const meta = productImageMap[p.id] || defaultProductMeta;
        return {
          ...p,
          image: p.image_url || meta.image,
          description: meta.description,
        };
      });
      setProducts(enriched);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSelect = (product) => {
    if (product.in_stock === false) return; // disabled
    setOrder({ ...order, product });
    navigate('/preparation');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red-400 text-xl">{error}</p>
      </div>
    );
  }

  if (!loading && products.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-xl text-muted-foreground">Geen producten beschikbaar</p>
        <p className="text-sm text-muted-foreground">Controleer de Supabase RLS-instellingen</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <ProgressBar currentStep={1} totalSteps={6} />
      <PageHeader 
        title="Kies je product"
        subtitle="Selecteer je favoriete verse kip"
      />

      <div className="container max-w-6xl mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.map((product, index) => {
            const outOfStock = product.in_stock === false;
            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
              >
                <Card
                  onClick={() => handleSelect(product)}
                  data-testid={`product-card-${product.id}`}
                  className={`product-card p-8 border-2 bg-card touch-feedback ${
                    outOfStock
                      ? 'border-border/40 opacity-50 cursor-not-allowed grayscale'
                      : `cursor-pointer hover:border-primary ${order.product?.id === product.id ? 'border-primary bg-primary/5' : 'border-border'}`
                  }`}
                >
                  <div className="flex items-center gap-6">
                    <div className="w-24 h-24 rounded-2xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary/10 to-primary/5 relative">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-2xl font-semibold text-foreground">{product.name}</h3>
                        {outOfStock && (
                          <span className="text-xs font-bold uppercase tracking-wider text-red-400 bg-red-500/10 px-2 py-1 rounded">
                            Uitverkocht
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground mb-3">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-primary">{'\u20AC'}{product.price.toFixed(2)}</span>
                        {!outOfStock && <ArrowRight className="w-6 h-6 text-muted-foreground" />}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductSelection;
